import type { RoolObject, RoolChannel } from '@rool-dev/sdk';

const objectCache = new Map<string, RoolObject>();
const locallyModifiedNodes = new Map<string, number>();

export function getCachedObjects(): ReadonlyMap<string, RoolObject> {
	return objectCache;
}

export function getCachedObject(objectId: string): RoolObject | undefined {
	return objectCache.get(objectId);
}

export async function loadAllObjects(sp: RoolChannel): Promise<void> {
	objectCache.clear();
	const { objects } = await sp.findObjects({});
	for (const obj of objects) {
		objectCache.set(obj.id, obj);
	}
}

export function cacheObject(obj: RoolObject): void {
	objectCache.set(obj.id, obj);
}

export function mergeObjectCache(
	objectId: string,
	partial: Record<string, unknown>,
): void {
	const existing = objectCache.get(objectId);
	if (existing) {
		const merged = { ...existing };
		for (const [key, value] of Object.entries(partial)) {
			if (value === null) {
				delete (merged as Record<string, unknown>)[key];
			} else {
				(merged as Record<string, unknown>)[key] = value;
			}
		}
		objectCache.set(objectId, merged as RoolObject);
	} else {
		objectCache.set(objectId, partial as RoolObject);
	}
}

export function patchCachedObject(
	objectId: string,
	patch: Record<string, unknown>,
): void {
	mergeObjectCache(objectId, { id: objectId, ...patch });
}

export function uncacheObject(objectId: string): void {
	objectCache.delete(objectId);
}

export function clearObjectCache(): void {
	objectCache.clear();
	locallyModifiedNodes.clear();
}

export function markNodeLocallyModified(nodeId: string, gracePeriodMs = 3000) {
	locallyModifiedNodes.set(nodeId, Date.now() + gracePeriodMs);
}

export function clearLocallyModifiedNodes() {
	locallyModifiedNodes.clear();
}

export function isNodeLocallyModified(nodeId: string): boolean {
	const expiry = locallyModifiedNodes.get(nodeId);
	if (!expiry) return false;
	if (Date.now() > expiry) {
		locallyModifiedNodes.delete(nodeId);
		return false;
	}
	return true;
}
